import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

type Table<T> = { [key: string]: T };

interface DatumProperties {
  datum: any;
  settingsSortIndex: number;
  settingsSetSortIndex: number;
  settingsDisabled?: any;
  settingsSetDisabled?: any;
}

const FOUNDATION_DATUM_VERTICAL_ANCHOR = [
  "LEVEL",
  "TOP_OF_SLAB",
  "BOTTOM_OF_SLAB",
  "BOTTOM_OF_FLOORPLATE",
  "CEILING_HEIGHT",
  "FOUNDATION",
];

const FACADE_HORIZONTAL_DATUM_ANCHOR = ["ROOM_EDGE", "STRUCTURE", "SUBSTRATE", "OUTSIDE_OF_SURFACE"];
const FACADE_VERTICAL_DATUM_ANCHOR = ["BASE_WALL", "GABLE_END"];
const DIST_EPSILON = 1e-6;

const isBuiltInDatum: Table<boolean> = {
  base_wall_bottom: true,
  base_wall_top: true,
  gable_end_bottom: true,
  gable_end_top: true,
  wall_start: true,
  wall_end: true,
};

const builtInDatumsGroupId = "builtInDatumsGroup";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_459: DocMigration = {
  maxSchemaVersion: "0.0.459",
  description: "Migrate datums",
  apply(json: any) {
    const {
      settings,
      settingsSets,
      levels,
      groups,
      entityGroups,
      facadeSweepStrata,
      facadeSpanStrata,
      entityRefs = {},
      datums = {},
    } = json;
    let sortIndex = Object.keys(datums).length;
    let groupSortIndex = 0;
    const affectedSettingsSets: { [settingsSetId: string]: boolean } = {};

    const updatedGroups = {
      ...groups,
    };

    const updatedEntityGroups = {
      ...entityGroups,
    };

    const updatedFacadeSweepStrata = {
      ...facadeSweepStrata,
    };

    const updatedFacadeSpanStrata = {
      ...facadeSpanStrata,
    };

    const updatedDatums = {
      ...datums,
    };

    const updatedEntityRefs = {
      ...entityRefs,
    };

    const updatedSettings = {
      ...settings,
    };

    const updatedSettingsSets = {
      ...settingsSets,
    };

    const deleteSettings = (settingsId: string, settingsSetId: string) => {
      delete updatedSettings[settingsId];
      delete updatedEntityRefs[settingsId];
      affectedSettingsSets[settingsSetId] = true;
    };

    const deleteSettingsSetIfEmpty = (settingsSetId: string) => {
      const hasChildSettings = Object.values(updatedSettings).some((s: any) => s.parentId === settingsSetId);

      if (hasChildSettings) {
        return;
      }

      delete updatedSettingsSets[settingsSetId];
      delete updatedEntityRefs[settingsSetId];
    };

    const generateNewDatumId = (oldDatumId: string, settingsId: string) => {
      return `${oldDatumId}_${settingsId}`;
    };

    // add built-in datums group
    updatedGroups[builtInDatumsGroupId] = {
      entityType: "GROUP",
      name: "Built-in Datums",
      id: builtInDatumsGroupId,
      sortIndex: groupSortIndex++,
      isBuiltIn: true,
    };

    // migrate legacy level datums
    for (const levelId in levels) {
      const level = levels[levelId];

      if (!level.levelDatums) {
        continue;
      }

      const levelDatumGroupId = `${levelId}_datums`;

      for (const datumId in level.levelDatums) {
        const { name, anchor, offset } = level.levelDatums[datumId];

        // create the datum in th new datums table
        updatedDatums[datumId] = {
          entityType: "DATUM",
          name: `${level.name} > ${name} > ${anchor}`,
          id: datumId,
          sortIndex: sortIndex++,
          datumType: "LEVEL",
          direction: "VERTICAL",
          justification: "BOTTOM_UP",
          offsetType: "RELATIVE",
          levelId,
          anchor,
          offset,
        };

        // determine the group for the datum
        const datumGroupId = isBuiltInDatum[datumId] ? builtInDatumsGroupId : levelDatumGroupId;

        // create the group if needed
        if (!updatedGroups[datumGroupId]) {
          updatedGroups[datumGroupId] = {
            entityType: "GROUP",
            name: `${level.name} Datums`,
            id: datumGroupId,
            sortIndex: groupSortIndex++,
          };
        }

        // add it to the corresponding group
        updatedEntityGroups[`datum.${datumId}`] = datumGroupId;
      }

      // remove the legacy datums from the levels
      delete level.levelDatums;
    }

    // migrate legacy foundation and facade datums
    const migratedCatalogDatums: Table<Table<DatumProperties>> = {};
    for (const settingsId in settings) {
      const setting = settings[settingsId];

      if (setting.type !== "DATUM_CATALOG_SETTINGS") {
        continue;
      }

      const {
        parentId: settingsSetId,
        name: settingsName,
        datums: datumsFromSettings,
        disabled: settingsDisabled,
        sortIndex: settingsSortIndex,
      } = setting;

      if (!datumsFromSettings) {
        deleteSettings(settingsId, settingsSetId);
        continue;
      }

      const docRefId = entityRefs[settingsSetId]?.docRefId;
      const docRefDisabled = entityRefs[settingsSetId]?.disabled;

      const {
        name: settingsSetName,
        disabled: settingsSetDisabled,
        sortIndex: settingsSetSortIndex,
      } = settingsSets[settingsSetId];

      for (const oldDatumId in datumsFromSettings) {
        const datumFromSettings = datumsFromSettings[oldDatumId];
        let newDatumId = oldDatumId;
        let migrated = false;

        if (isFoundationDatum(datumFromSettings)) {
          const isLateral = isLateralDatum(datumFromSettings);
          const { name, anchor, justification, offset, offsetNormalized } = datumFromSettings;

          if (updatedDatums[newDatumId]) {
            console.warn(`Datum with id ${newDatumId} already exists in datums. Generating new id.`);

            // generate a new stable id for the datum having a id collision
            newDatumId = generateNewDatumId(oldDatumId, settingsId);
          }

          const migratedFoundationDatumGroupId = `${settingsId}_foundation_datums`;
          const migratedFoundationDatumGroupName = settingsSetName;

          updatedDatums[newDatumId] = {
            entityType: "DATUM",
            name,
            id: newDatumId,
            srcSettingsId: settingsId,
            srcSettingsSetId: settingsSetId,
            srcSettingsName: settingsName,
            srcSettingsSetName: settingsSetName,
            sortIndex: sortIndex++,
            datumType: "FOUNDATION",
            direction: isLateral ? "LATERAL" : "VERTICAL",
            justification,
            offsetType: "RELATIVE",
            anchor: anchor ?? (isLateral ? "OUTSIDE_FACE_OF_SUBSTRATE" : "TOP_OF_SLAB"),
            offset,
            offsetNormalized,
          };

          // determine the group for the datum
          const datumGroupId = isBuiltInDatum[oldDatumId] ? builtInDatumsGroupId : migratedFoundationDatumGroupId;

          // create the group if needed
          if (!updatedGroups[datumGroupId]) {
            updatedGroups[datumGroupId] = {
              entityType: "GROUP",
              name: migratedFoundationDatumGroupName,
              id: datumGroupId,
              sortIndex: groupSortIndex++,
            };
          }

          // add it to the corresponding group
          updatedEntityGroups[`datum.${newDatumId}`] = datumGroupId;

          migrated = true;
        } else if (isFacadeDatum(datumFromSettings)) {
          const isHorizontal = isHorizontalDatum(datumFromSettings);
          const { name, anchor, justification, offset, offsetNormalized } = datumFromSettings;

          if (updatedDatums[newDatumId]) {
            console.warn(`Datum with id ${newDatumId} already exists in datums. Generating new id.`);

            // generate a new stable id for the datum having a id collision
            newDatumId = generateNewDatumId(oldDatumId, settingsId);
          }

          const migratedFacadeDatumGroupId = `${settingsId}_facade_datums`;
          const migratedFacadeDatumGroupName = settingsSetName;

          updatedDatums[newDatumId] = {
            entityType: "DATUM",
            name,
            id: newDatumId,
            srcSettingsId: settingsId,
            srcSettingsSetId: settingsSetId,
            srcSettingsName: settingsName,
            srcSettingsSetName: settingsSetName,
            sortIndex: sortIndex++,
            datumType: "WALL",
            direction: isHorizontal ? "HORIZONTAL" : "VERTICAL",
            justification,
            offsetType: "RELATIVE",
            anchor: anchor ?? (isHorizontal ? "OUTSIDE_OF_SURFACE" : "BASE_WALL"),
            offset,
            offsetNormalized,
          };

          // determine the group for the datum
          const datumGroupId = isBuiltInDatum[oldDatumId] ? builtInDatumsGroupId : migratedFacadeDatumGroupId;

          // create the group if needed
          if (!updatedGroups[datumGroupId]) {
            updatedGroups[datumGroupId] = {
              entityType: "GROUP",
              name: migratedFacadeDatumGroupName,
              id: datumGroupId,
              sortIndex: groupSortIndex++,
            };
          }

          // add it to the corresponding group
          updatedEntityGroups[`datum.${newDatumId}`] = datumGroupId;

          migrated = true;
        }

        if (!migrated && !isBuiltInDatum[oldDatumId]) {
          console.warn(`Datum with id ${oldDatumId} is not a foundation or facade datum. Skipping.`);
          continue;
        }

        // if the settings were doc-ref'd and the datum is not built-in,
        // add the migrated datum to the entity refs
        if (docRefId && !isBuiltInDatum[oldDatumId]) {
          updatedEntityRefs[newDatumId] = {
            id: newDatumId,
            docRefId,
            entityType: "DATUM",
            disabled: docRefDisabled,
            sortIndex,
          };
        }

        if (!migratedCatalogDatums[oldDatumId]) {
          migratedCatalogDatums[oldDatumId] = {};
        }

        const datumHash = getDatumHash(datumFromSettings);

        // skip if this datum was already migrated with the same hash
        if (migratedCatalogDatums[oldDatumId][datumHash]) {
          continue;
        }

        migratedCatalogDatums[oldDatumId][datumHash] = {
          datum: updatedDatums[newDatumId],
          settingsSortIndex,
          settingsSetSortIndex,
          settingsDisabled,
          settingsSetDisabled,
        };
      }

      deleteSettings(settingsId, settingsSetId);
    }

    // delete settings sets that are empty
    for (const settingsSetId in affectedSettingsSets) {
      deleteSettingsSetIfEmpty(settingsSetId);
    }

    // filter out duplicated datums with same conditions
    const filteredMigratedDatums: { [datumId: string]: DatumProperties[] } = {};
    for (const datumId in migratedCatalogDatums) {
      const datumProperties = Object.values(migratedCatalogDatums[datumId]).sort((a, b) => {
        const settingsSetsDiff = a.settingsSetSortIndex - b.settingsSetSortIndex;
        if (settingsSetsDiff !== 0) {
          return settingsSetsDiff;
        }

        return a.settingsSortIndex - b.settingsSortIndex;
      });

      // skip if this datum was having a single appearance
      if (datumProperties.length <= 1) {
        continue;
      }

      let firstDatumProperties = datumProperties[0];
      const restDatumProperties = datumProperties.slice(1);
      const hasCollision = restDatumProperties.some((d) => compareDatums(d, firstDatumProperties));

      // skip if there is no collision
      if (!hasCollision) {
        continue;
      }

      let firstIndex = 0;
      for (let i = 1; i < datumProperties.length; i++) {
        if (
          compareDisabled(datumProperties[i].settingsDisabled, firstDatumProperties.settingsDisabled) &&
          compareDisabled(datumProperties[i].settingsSetDisabled, firstDatumProperties.settingsSetDisabled)
        ) {
          firstDatumProperties = datumProperties[i];
          firstIndex = i;
        }
      }

      filteredMigratedDatums[datumId] = datumProperties.slice(firstIndex);
    }

    // look for collisions
    for (const datumId in filteredMigratedDatums) {
      const datumProperties = filteredMigratedDatums[datumId];

      // skip empty datum properties
      if (datumProperties.length === 0) {
        continue;
      }

      const firstDatumProperties = datumProperties[0];
      const restDatumProperties = datumProperties.slice(1);

      if (!isFacadeDatum(firstDatumProperties.datum)) {
        // skip if this datum is not a facade datum
        continue;
      }

      // look for this datum on the facade sweeps
      for (const facadeSweepId in facadeSweepStrata) {
        const facadeSweep = facadeSweepStrata[facadeSweepId];
        const { datum, disabled: facadeSweepDisabled } = facadeSweep;

        if (datum !== datumId) {
          continue;
        }

        // set the conditions for the first datum appearance
        facadeSweep.datum = firstDatumProperties.datum.id;
        facadeSweep.disabled = mergeDisabled(
          facadeSweepDisabled,
          mergeDisabled(firstDatumProperties.settingsDisabled, firstDatumProperties.settingsSetDisabled),
        );

        // duplicate facade sweep for the rest of the appearances
        for (const datumProperties of restDatumProperties) {
          const newFacadeSweepId = uid();
          updatedFacadeSweepStrata[newFacadeSweepId] = {
            ...facadeSweep,
            id: newFacadeSweepId,
            name: `${facadeSweep.name} (migrated)`,
            datum: datumProperties.datum.id,
            disabled: mergeDisabled(
              facadeSweepDisabled,
              mergeDisabled(datumProperties.settingsDisabled, datumProperties.settingsSetDisabled),
            ),
          };
        }
      }

      // look for this datum on the facade spans
      for (const facadeSpanId in facadeSpanStrata) {
        const facadeSpan = facadeSpanStrata[facadeSpanId];
        const { lowerDatum, upperDatum, startDatum, endDatum, disabled: facadeSpanDisabled } = facadeSpan;

        if (lowerDatum !== datumId && upperDatum !== datumId && startDatum !== datumId && endDatum !== datumId) {
          continue;
        }

        // set the conditions for the first datum appearance
        facadeSpan.disabled = mergeDisabled(
          facadeSpanDisabled,
          mergeDisabled(firstDatumProperties.settingsDisabled, firstDatumProperties.settingsSetDisabled),
        );
        facadeSpan.lowerDatum = lowerDatum === datumId ? firstDatumProperties.datum.id : lowerDatum;
        facadeSpan.upperDatum = upperDatum === datumId ? firstDatumProperties.datum.id : upperDatum;
        facadeSpan.startDatum = startDatum === datumId ? firstDatumProperties.datum.id : startDatum;
        facadeSpan.endDatum = endDatum === datumId ? firstDatumProperties.datum.id : endDatum;

        // duplicate facade span for the rest of the appearances
        for (const datumProperties of restDatumProperties) {
          const newFacadeSpanId = uid();
          updatedFacadeSpanStrata[newFacadeSpanId] = {
            ...facadeSpan,
            id: newFacadeSpanId,
            name: `${facadeSpan.name} (migrated)`,
            lowerDatum: lowerDatum === datumId ? datumProperties.datum.id : lowerDatum,
            upperDatum: upperDatum === datumId ? datumProperties.datum.id : upperDatum,
            startDatum: startDatum === datumId ? datumProperties.datum.id : startDatum,
            endDatum: endDatum === datumId ? datumProperties.datum.id : endDatum,
            disabled: mergeDisabled(
              facadeSpanDisabled,
              mergeDisabled(datumProperties.settingsDisabled, datumProperties.settingsSetDisabled),
            ),
          };
        }
      }
    }

    return {
      ...json,
      groups: updatedGroups,
      datums: updatedDatums,
      entityGroups: updatedEntityGroups,
      facadeSweepStrata: updatedFacadeSweepStrata,
      facadeSpanStrata: updatedFacadeSpanStrata,
      entityRefs: updatedEntityRefs,
      settings: updatedSettings,
      settingsSets: updatedSettingsSets,
    };
  },
};

function isFoundationDatum(d: any): boolean {
  return FOUNDATION_DATUM_VERTICAL_ANCHOR.includes(d?.anchor) || isLateralDatum(d);
}

function isFacadeDatum(d: any): boolean {
  if (!d) {
    return false;
  }

  if (isHorizontalDatum(d)) {
    // facade horizontal datums are allowed to have no anchor
    return d.anchor === undefined || FACADE_HORIZONTAL_DATUM_ANCHOR.includes(d?.anchor);
  }

  return FACADE_VERTICAL_DATUM_ANCHOR.includes(d?.anchor);
}

function isLateralDatum(d: any): d is boolean {
  return d?.datumType === "LATERAL";
}

function isHorizontalDatum(d: any): boolean {
  return d?.datumType === "HORIZONTAL";
}

function compareDatums(datum1: any, datum2: any): boolean {
  return (
    datum1.anchor === datum2.anchor &&
    datum1.justification === datum2.justification &&
    Math.abs((datum1.offset ?? 0) - (datum2.offset ?? 0)) < DIST_EPSILON &&
    datum1.offsetNormalized === datum2.offsetNormalized
  );
}

function getDatumHash(datum: any): string {
  const datumType = isFoundationDatum(datum) ? "FOUNDATION" : "FACADE";
  return (
    datumType +
    "_" +
    (datum.anchor ?? "undefined") +
    "_" +
    (datum.justification ?? "undefined") +
    "_" +
    (datum.offsetNormalized ?? "false") +
    "_" +
    Math.floor((datum.offset ?? 0) * 1000) / 1000
  );
}

function isFormula(v: any) {
  return !!v && typeof v === "object" && v.hasOwnProperty("type") && v.type === "FORMULA";
}

function booleanToFormula(v: boolean) {
  return formulaNew(v ? "true" : "false");
}

function formulaNew(formula: string) {
  return { type: "FORMULA", formula };
}

function mergeDisabled(disabled1?: boolean | any, disabled2?: boolean | any): any {
  if (disabled1 === undefined) {
    return disabled2;
  }

  if (disabled2 === undefined) {
    return disabled1;
  }

  const formula1 = isFormula(disabled1) ? disabled1 : booleanToFormula(disabled1);
  const formula2 = isFormula(disabled2) ? disabled2 : booleanToFormula(disabled2);

  return formulaNew(`(${formula1.formula}) and (${formula2.formula})`);
}

function compareDisabled(disabled1?: boolean | any, disabled2?: boolean | any): boolean {
  if (disabled1 === undefined) {
    return disabled2 === undefined;
  }

  if (disabled2 === undefined) {
    return disabled1 === undefined;
  }

  const formula1 = isFormula(disabled1) ? disabled1 : booleanToFormula(disabled1);
  const formula2 = isFormula(disabled2) ? disabled2 : booleanToFormula(disabled2);

  return formula1.formula === formula2.formula;
}

export default migration_0_0_459;
